import { index, integer, jsonb, pgTable, text, timestamp, uuid, varchar } from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { id } from './_shared';
import { users } from './users';

// =============================================================================
// PROMO TOKENS — single-use (or capped-use) redemption codes that, on
// redemption, mint a REAL entitlement_grants row (apps/api/lib/entitlements.ts's
// existing G1 promo-grant mechanism — see entitlement_grants.ts's own module
// header). This table does NOT reshape or duplicate that machinery: redemption
// writes exactly the same `entitlement_grants` insert a human would otherwise
// type by hand from docs/design/g1-entitlement-grants-neon.sql's own template —
// a promo token is simply an AUTOMATED, per-channel-labeled way to trigger that
// same insert, gated by a human-typeable code instead of a human running SQL.
//
// THE MARKETING USE (the owner's own spec): the marketing stream hands these
// out per channel ("reddit-launch-week", "founder-gift") as the tracker-free
// conversion signal — a code redeemed IS the conversion event, no pixel/UTM
// needed. Founder codes seed early users the same way entitlement_grants'
// own "founder" grant already seeds Jason's account (g1-entitlement-grants-
// neon.sql's concrete INSERT).
//
// SECRETS DISCIPLINE (rule #9 spirit, applied to a non-secret-service value on
// purpose): the plaintext code is shown to its creator EXACTLY ONCE, at mint
// time, and is NEVER persisted or logged anywhere thereafter — only its
// SHA-256 hex digest (code_hash) is stored, mirroring this codebase's own
// sha256Hex idiom (AlignmentEssayService.ts / MemoryLedgerDO.ts's
// hashEntry) via apps/api/lib/promoCode.ts's hashCode. A plaintext code
// leaking from a DB dump or a log line would be exactly as bad as a leaked
// password — so it is treated with that same never-at-rest posture, even
// though it is not a SecretsService-managed secret proper (it identifies a
// PROMOTION, not a user credential).
//
// Additive only (rule #11): CREATE TABLE IF NOT EXISTS via the Neon tool
// (docs/design/promo-tokens-neon.sql), mirrored here, exported from index.ts.
// Nothing dropped or renamed. Kill switch is `disabledAt` (nothing-
// unrecoverable) — a compromised or mistakenly-issued code is DISABLED, never
// deleted; its full redemption history stays queryable forever.
// =============================================================================

/**
 * One mintable code. `codeHash` is the ONLY on-disk trace of the code itself
 * (SHA-256 hex, 64 chars — see module header); the plaintext is generated
 * server-side, shown once at creation, and never stored or logged again.
 */
export const promoTokens = pgTable(
  'promo_tokens',
  {
    id: id(),
    /** SHA-256 hex digest of the NORMALIZED code (uppercase, dashes/case-
     *  insensitive — apps/api/lib/promoCode.ts's normalizeCode). NEVER the
     *  plaintext code (see module header) — UNIQUE so a redeem lookup is a
     *  single indexed equality read, and so two independently-minted tokens
     *  can never collide on the same human-typeable string. */
    codeHash: varchar('code_hash', { length: 64 }).notNull().unique(),
    /** Free-text "what/why" a human names at mint time, e.g.
     *  'reddit-launch-week', 'founder-gift' — DATA, not an enum, mirroring
     *  entitlementGrants.label's own "used dynamically with different
     *  promos" reasoning (a new channel/campaign is a new string here, never
     *  a schema change). Nullable: a code can be minted with no label yet
     *  attached (never invented if the founder didn't type one). */
    label: text('label'),
    /** The entitlement grant SHAPE to create on redemption — mirrors
     *  entitlementGrants' own real columns (label/startsAt/endsAt), so
     *  redeeming a token is a byte-for-byte insert into entitlement_grants
     *  using exactly this payload (plus the redeeming user's own id, plus a
     *  clock-derived startsAt/endsAt when the payload only carries a
     *  duration — see apps/api/lib/promoCode.ts's buildGrantValues for the
     *  ONE place that translates this jsonb shape into a real
     *  entitlement_grants insert). NOT a NEW parallel grant vocabulary: the
     *  only fields this shape is ever allowed to carry are the ones
     *  entitlement_grants itself has (grantLabel, plus EITHER an explicit
     *  endsAt/durationDays to compute one) — never a bespoke
     *  tier/capability list of its own (this app's trial===paid capability
     *  invariant is not something a promo token's payload can override). */
    grantPayload: jsonb('grant_payload')
      .$type<{
        /** The label the MINTED entitlement_grants row will carry (rendered
         *  verbatim to the user, same as every other grant — Settings.tsx's
         *  existing GrantNote). Defaults to this token's own `label` at
         *  mint time when omitted (see promo.routes.ts POST /mint), so a
         *  redeemed grant is never labeled with an empty string. */
        grantLabel: string;
        /** Whole-day duration from the moment of REDEMPTION (not mint) — the
         *  brief's own "7 days, 2 weeks, 1 month" framing. Mutually
         *  exclusive with explicitEndsAt below; exactly one must be present
         *  (enforced by promo.routes.ts's mint-time zod validation, not by
         *  the column itself — jsonb has no CHECK constraint here, additive-
         *  only law). */
        durationDays?: number;
        /** An explicit, absolute ISO endsAt instead of a relative duration —
         *  for a promo tied to a real calendar date (e.g. "through the end
         *  of launch week") rather than a per-redeemer rolling window. */
        explicitEndsAt?: string;
      }>()
      .notNull(),
    /** How many DISTINCT users may redeem this code before it's exhausted.
     *  Defaults to 1 (true single-use, the mission's own headline shape) —
     *  a founder/campaign code can be minted with a higher cap for a
     *  multi-person promo without changing this table's shape. */
    maxUses: integer('max_uses').notNull().default(1),
    /** Incremented atomically on each successful redemption (see
     *  promo.routes.ts POST /redeem's single guarded UPDATE) — the
     *  authoritative "how many times has this been used" counter; never
     *  re-derived by counting promo_redemptions rows at read time, so the
     *  WHERE-guarded UPDATE and this column can never disagree about
     *  exhaustion. */
    useCount: integer('use_count').notNull().default(0),
    /** NULL = never expires. Set for a time-boxed promo (e.g. "reddit-
     *  launch-week" only valid that week) — checked at redemption time
     *  alongside disabledAt and the max_uses guard. */
    expiresAt: timestamp('expires_at', { withTimezone: true }),
    /** The founder/operator who minted this code. No hard FK cascade
     *  (mirrors entitlement_grants.userId's own no-FK posture for a promo/
     *  comp-adjacent table) — REFERENCES only, application-enforced
     *  cascade-free deletion semantics; this codebase's newest tables
     *  (drive_export.ts) DO take a real FK with users.id, so this one does
     *  too, matching that current-law precedent for a brand-new table. */
    createdBy: uuid('created_by').references(() => users.id),
    createdAt: timestamp('created_at', { withTimezone: true }).notNull().default(sql`now()`),
    /** Kill switch — set the instant a human disables this code early.
     *  NULL = live. Nothing-unrecoverable: disabling never deletes the row,
     *  its full mint/redemption history stays on the record permanently,
     *  exactly like entitlementGrants.revokedAt's own contract. */
    disabledAt: timestamp('disabled_at', { withTimezone: true }),
  },
  (t) => ({
    createdByIdx: index('promo_tokens_created_by_idx').on(t.createdBy),
  }),
);

/**
 * One redemption event — (token, user) is UNIQUE, so a single user can never
 * redeem the SAME token twice (the honest "already-redeemed-by-you" error
 * shape in promo.routes.ts derives directly from this constraint, not from a
 * separate application-level check that could drift from it). Never deleted
 * (nothing-unrecoverable) — this is the permanent audit trail of who redeemed
 * what, when.
 */
export const promoRedemptions = pgTable(
  'promo_redemptions',
  {
    id: id(),
    /** No hard FK to promo_tokens.id declared here (mirrors this table's own
     *  sibling posture: entitlement_grants/drive_export both vary on FK use
     *  per-column rather than uniformly; the (token_id, user_id) UNIQUE
     *  constraint below is the structural guarantee this table exists to
     *  provide, not referential integrity to promo_tokens specifically) —
     *  application-enforced via promo.routes.ts's own token lookup. */
    tokenId: uuid('token_id').notNull(),
    userId: uuid('user_id')
      .notNull()
      .references(() => users.id, { onDelete: 'cascade' }),
    redeemedAt: timestamp('redeemed_at', { withTimezone: true }).default(sql`now()`),
  },
  (t) => ({
    tokenUserIdx: index('promo_redemptions_token_user_idx').on(t.tokenId, t.userId),
    userIdx: index('promo_redemptions_user_idx').on(t.userId),
  }),
);
